<template>
  <div class="signature-wrapper">
    <!-- canvas pequeño -->
    <canvas
      ref="canvasElSmall"
      class="signature-canvas border border-gray-400 rounded-md"
      width="250"
      height="100"
    ></canvas>

    <div class="signature-actions">
      <ButtonDefault @click="clearPadSmall">Borrar</ButtonDefault>
      <ButtonDefault color="blue" @click="savePad">Guardar</ButtonDefault>
      <ButtonDefault color="blue" @click="openFull">
        <MdiIcon icono="mdiFullscreen" />
      </ButtonDefault>
    </div>
  </div>

  <!-- MODAL PANTALLA COMPLETA -->
  <Teleport to="body">
    <div
      v-if="isFull"
      class="fixed inset-0 z-[9999] flex flex-col bg-black/80 p-4"
    >
      <div class="flex justify-between items-center text-white mb-2">
        <span class="text-sm">Firma en pantalla completa</span>
        <div class="flex gap-2">
          <ButtonDefault color="blue" @click="clearPadFull">Borrar</ButtonDefault>
          <ButtonDefault color="green" @click="savePadFull">Guardar</ButtonDefault>
          <ButtonDefault color="red" @click="closeFull">Cerrar</ButtonDefault>
        </div>
      </div>

      <div class="flex-1 flex justify-center items-center">
        <!-- canvas grande ocupa la mayor parte de la pantalla -->
        <canvas
          ref="canvasElFull"
          class="bg-white rounded-md border border-white w-full h-full max-w-[90vw] max-h-[70vh] touch-none cursor-crosshair"
        ></canvas>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const { setFoto } = defineProps<{
  setFoto: (foto: string) => void
}>()

// Estado UI
const isFull = ref(false)
const drawing = ref(false)
const currentCtx = ref<CanvasRenderingContext2D | null>(null) // ctx activo según modal o pequeño

// Refs canvas
const canvasElSmall = ref<HTMLCanvasElement | null>(null)
const canvasElFull = ref<HTMLCanvasElement | null>(null)

// ctx individuales
const ctxSmall = ref<CanvasRenderingContext2D | null>(null)
const ctxFull = ref<CanvasRenderingContext2D | null>(null)

/* ---------- estilo del lápiz ---------- */
function setStyle(ctx: CanvasRenderingContext2D | null, scale = 1) {
  if (!ctx) return
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#000'
  ctx.lineWidth = 2 * scale
}

/* ---------- coords relativas ---------- */
function getPos(evt: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect()
  if (evt instanceof TouchEvent) {
    const touch = evt.touches[0] || evt.changedTouches[0]
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }
  } else {
    return {
      x: (evt as MouseEvent).offsetX,
      y: (evt as MouseEvent).offsetY,
    }
  }
}

/* ---------- handlers de dibujo ---------- */
function startDraw(evt: MouseEvent | TouchEvent) {
  const canvas = getActiveCanvas()
  if (!canvas || !currentCtx.value) return
  drawing.value = true
  const { x, y } = getPos(evt, canvas)
  currentCtx.value.beginPath()
  currentCtx.value.moveTo(x, y)
  evt.preventDefault()
}

function draw(evt: MouseEvent | TouchEvent) {
  if (!drawing.value || !currentCtx.value) return
  const canvas = getActiveCanvas()
  if (!canvas) return
  const { x, y } = getPos(evt, canvas)
  currentCtx.value.lineTo(x, y)
  currentCtx.value.stroke()
  evt.preventDefault()
}

function stopDraw(evt: MouseEvent | TouchEvent) {
  if (!drawing.value || !currentCtx.value) return
  currentCtx.value.closePath()
  drawing.value = false
  evt.preventDefault()
}

/* ---------- listeners ---------- */
function addListeners(canvas: HTMLCanvasElement) {
  canvas.addEventListener('mousedown', startDraw)
  canvas.addEventListener('mousemove', draw)
  canvas.addEventListener('mouseup', stopDraw)
  canvas.addEventListener('mouseleave', stopDraw)

  canvas.addEventListener('touchstart', startDraw, { passive: false })
  canvas.addEventListener('touchmove', draw, { passive: false })
  canvas.addEventListener('touchend', stopDraw, { passive: false })

  // evita scroll táctil
  canvas.style.touchAction = 'none'
  canvas.classList.add('touch-none')
}

function removeListeners(canvas: HTMLCanvasElement) {
  canvas.removeEventListener('mousedown', startDraw)
  canvas.removeEventListener('mousemove', draw)
  canvas.removeEventListener('mouseup', stopDraw)
  canvas.removeEventListener('mouseleave', stopDraw)

  canvas.removeEventListener('touchstart', startDraw)
  canvas.removeEventListener('touchmove', draw)
  canvas.removeEventListener('touchend', stopDraw)
}

/* ---------- util canvas activo ---------- */
function getActiveCanvas(): HTMLCanvasElement | null {
  return isFull.value ? canvasElFull.value : canvasElSmall.value
}

/* ---------- limpiar ---------- */
function clearPadSmall() {
  if (!canvasElSmall.value || !ctxSmall.value) return
  ctxSmall.value.clearRect(0, 0, canvasElSmall.value.width, canvasElSmall.value.height)
}

function clearPadFull() {
  if (!canvasElFull.value || !ctxFull.value) return
  ctxFull.value.clearRect(0, 0, canvasElFull.value.width, canvasElFull.value.height)
}

/* ---------- exportar firma ---------- */
function getDataUrl(canvas: HTMLCanvasElement | null): string | null {
  if (!canvas) return null
  return canvas.toDataURL('image/png')
}

function getBase64Only(canvas: HTMLCanvasElement | null): string | null {
  const full = getDataUrl(canvas)
  if (!full) return null
  const parts = full.split(',')
  return parts[1] ?? null
}

function savePad() {
  // usa el canvas chico como versión final persistida
  const base64 = getBase64Only(canvasElSmall.value)
  if (!base64) return
  setFoto('data:image/jpeg;base64,' + base64)
}
function savePadFull() {
  // usa el canvas chico como versión final persistida
  const base64 = getBase64Only(canvasElFull.value)
  if (!base64) return
  closeFull()
  setFoto('data:image/jpeg;base64,' + base64)
}
/* ---------- copiar imagen entre canvas ---------- */
/**
 * Copia contenido de A -> B, escalando.
 * Escala por drawImage porque los tamaños son distintos.
 */
function copyCanvas(src: HTMLCanvasElement, dst: HTMLCanvasElement) {
  const dstCtx = dst.getContext('2d')
  if (!dstCtx) return
  dstCtx.clearRect(0, 0, dst.width, dst.height)
  dstCtx.drawImage(src, 0, 0, src.width, src.height, 0, 0, dst.width, dst.height)
}

/* ---------- abrir modal fullscreen-like ---------- */
async function openFull() {
  isFull.value = true

  // esperar a que el modal se pinte para poder medir canvas grande
  await nextTick()

  // configurar canvas grande dinámicamente
  if (canvasElFull.value && canvasElSmall.value) {
    // dar tamaño grande al canvas real en píxeles
    canvasElFull.value.width = window.innerWidth * 0.9
    canvasElFull.value.height = window.innerHeight * 0.7

    const scale =
  (canvasElFull.value.width / canvasElSmall.value.width +
   canvasElFull.value.height / canvasElSmall.value.height) / 2

  ctxFull.value = canvasElFull.value.getContext('2d')
  setStyle(ctxFull.value, scale)

    // copiar lo que había en el pequeño
    if (canvasElSmall.value) {
      copyCanvas(canvasElSmall.value, canvasElFull.value)
    }

    currentCtx.value = ctxFull.value
    addListeners(canvasElFull.value)
  }
}

/* ---------- cerrar modal ---------- */
function closeFull() {
  // copiar de vuelta lo dibujado grande al pequeño con escalado inverso
  if (canvasElFull.value &&  canvasElSmall.value) copyCanvas(canvasElFull.value, canvasElSmall.value)

  // limpiar listeners del canvas grande
  if (canvasElFull.value) {
    removeListeners(canvasElFull.value)
  }

  // volver a usar el canvas chico como activo
  currentCtx.value = ctxSmall.value

  isFull.value = false
}

/* ---------- ciclo de vida ---------- */
onMounted(() => {
  // init canvas pequeño
  if (!canvasElSmall.value) return
  ctxSmall.value = canvasElSmall.value.getContext('2d')
  if (!ctxSmall.value) return
  setStyle(ctxSmall.value)

  currentCtx.value = ctxSmall.value
  addListeners(canvasElSmall.value)
})

onBeforeUnmount(() => {
  if (canvasElSmall.value) removeListeners(canvasElSmall.value)
  if (canvasElFull.value) removeListeners(canvasElFull.value)
})
</script>

<style scoped>
.signature-wrapper {
  display: inline-flex;
  flex-direction: column;
  gap: 0.5rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
}

.signature-canvas {
  background: #fff;
  cursor: crosshair;
  touch-action: none;
}

.signature-actions {
  display: flex;
  gap: 0.5rem;
}

/* modal */
.touch-none {
  touch-action: none;
}

/* Tailwind classes usadas en el modal:
fixed inset-0 z-[9999] flex flex-col bg-black/80 p-4
flex justify-between items-center text-white mb-2
flex-1 flex justify-center items-center
bg-white rounded-md border border-white
*/
</style>
